import { Button, Group, Text } from "@mantine/core"
import React from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { mutate } from "swr"
import { OrderApi } from "../../apis"
import { IAuth } from "../../store/Auth"
import { currencyFormat } from "../../utils"
import { message } from "../../utils/message"
import { OrderActionCard } from "../order/order-action-card"
import { useSalesActions } from "../order/order-actions"
import { ReasonTooltip } from "../ui"

/** Бөөнөөр батлах: approves every selected PO that is «Ирсэн» and addressed to this business. */
export function PurchaseSalesSOForm({ action, onCancel, reload, setChecked, onBusyChange }: { action: any[]; onCancel: () => void; reload: any; setChecked?: any; onBusyChange?: (busy: boolean) => void }) {
  const { t } = useTranslation(["order", "supplierSales", "common"])
  const user = useSelector((state: { auth: IAuth }) => state.auth.user)
  const [loading, setLoading] = React.useState<boolean>(false)
  const { permissionState } = useSalesActions()
  const perm = permissionState("approve")

  const orders = (action[1] || []).filter((f: any) => f.orderStatus === "REVIEWED" && user?.currentBusiness?.id === f?.receiverBusiness?.id)
  const total = orders.reduce((sum: number, o: any) => sum + Number(o.orderAmount || 0), 0)

  const cancel = () => {
    if (!loading) onCancel?.()
  }

  const onSubmit = async () => {
    if (loading) return
    let ok = false
    setLoading(true)
    onBusyChange?.(true)
    try {
      const res = await OrderApi.respond_bulk(orders.map((c: any) => c.id))
      mutate("swr.order.count.customer")
      mutate("swr.order.count.sales")
      setChecked?.([])

      // Each order is approved on its own: some can succeed while others are rejected (no stock,
      // unconfigured payment term). Say so instead of a blanket success.
      const failed = res?.failed || []
      if (failed.length > 0) {
        message.error(
          t("order:purchaseSales.bulkApprovePartial", {
            approved: res?.approvedCount ?? 0,
            failed: failed.length,
            codes: failed.map((f: any) => f.code || f.orderId).join(", "),
          }),
        )
      } else {
        message.success(t("common:messages.requestSuccess"))
      }
      reload()
      ok = true
    } catch (err: any) {
      message.error(err.message || t("common:messages.requestFailed"))
    } finally {
      setLoading(false)
      onBusyChange?.(false)
    }
    // A failed request keeps the dialog open, as before; close only after the run finished.
    if (ok) onCancel?.()
  }

  return (
    <OrderActionCard
      title={t("supplierSales:purchase.bulkApprove")}
      onClose={cancel}
      loading={loading}
      footer={
        <>
          <Button variant="default" onClick={cancel}>{t("common:actions.dismiss")}</Button>
          <ReasonTooltip reason={perm.reason}>
            <Button onClick={onSubmit}>{t("order:purchaseSales.approve")}</Button>
          </ReasonTooltip>
        </>
      }
    >
      <Text fz={14}>{t("order:purchaseSales.confirmBulkApproveDesc")}</Text>
      <Group justify="space-between" wrap="nowrap">
        <Text fz={14} c="gray.8">{t("supplierSales:purchase.bulkCount")}</Text>
        <Text fz={14} fw={700}>{orders.length}</Text>
      </Group>
      <Group justify="space-between" wrap="nowrap">
        <Text fz={14} c="gray.8">{t("order:purchaseSales.orderAmount")}</Text>
        <Text fz={14} fw={700} ff="monospace">{currencyFormat(total)}</Text>
      </Group>
    </OrderActionCard>
  )
}
